<script>
import AkTitleBackgroundText from '@components/v2/general/AkTitleBackgroundText.vue';
import AkButtonValidationV2 from '@components/v2/general/AkButtonValidationV2.vue';
import RatingStar from '@views/revenuemanagement/RatingStar.vue';
import AkShimmer from '@components/v2/general/AkShimmer.vue';
import ReservationSiteConst from '@mixins/const/reservationSiteConst';
import ReviewMixin from '@mixins/reviewMixin';
import UtilsMixin from '@mixins/utilsMixin';
import reviewService from '@services/reviewService';
import PermissionsMixinComputed from '@mixins/permissionsMixinComputed';
import MessageMixin from '@mixins/messageMixin';

export default {
  name: 'ReviewStats',
  mixins: [ReservationSiteConst, ReviewMixin, UtilsMixin, PermissionsMixinComputed, MessageMixin],
  components: {AkShimmer, RatingStar, AkButtonValidationV2, AkTitleBackgroundText},
  props: {
    filter: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      stats: [null, null, null, null],
    };
  },
  computed: {
    globalRating() {
      const validStats = this.stats.filter((s) => s !== null);
      if (!validStats.length) return '-';
      const totalReviews = validStats.reduce((sum, s) => sum + s.nbTotalReviews, 0);
      if (!totalReviews) return null;
      const weightedSum = validStats.reduce((sum, s) => sum + (s.globalRating / s.scale) * 10 * s.nbTotalReviews, 0);
      return this.formatAmountVirgule(weightedSum / totalReviews);
    },
    totalReviewCount() {
      const validStats = this.stats.filter((s) => s !== null);
      if (!validStats.length) return 0;
      return validStats.reduce((sum, s) => sum + s.nbTotalReviews, 0);
    },
  },
  mounted() {
    this.search();
  },
  methods: {
    platformScroll(direction) {
      this.$refs.platformScrollContainer.scrollBy({
        left: direction * (Math.max(this.$refs.platformScrollContainer.offsetWidth, 1400) / 1.6),
        behavior: 'smooth',
      });
    },
    search() {
      const count = this.stats.filter(Boolean).length || 4;
      this.stats = Array(count).fill(null);
      reviewService.reviewsStats(this.filter).then((data) => {
        this.stats = data;
      });
    },
    downloadExport() {
      let promise;
      if (this.isOwner) {
        promise = reviewService.exportListForOwner(this.filter);
      } else {
        promise = reviewService.exportList(this.filter);
      }
      Promise.all([promise])
        .catch((e) => {
          this.addError(this.$t('unknow_error') + ' : ' + e.message);
        })
        .finally(() => {
          this.$refs.downloadButton.init();
        });
    },
  },
};
</script>

<template>
  <div class="flex flex-col md:gap-4 gap-6 md:pb-12 pb-6 pt-6 bg-secondary-50 -mx-5 -mt-5 h-full">
    <div class="flex gap-6 md:items-center px-4 md:px-12">
      <div class="flex gap-2 items-center">
        <i class="ga-icon ga-star_sharp text-[21px] text-grey-900" />
        <span>
          <AkTitleBackgroundText text="Mes __avis__" class-name="text-3xl text-grey-900 font-bold" />
        </span>
      </div>
      <AkButtonValidationV2
        ref="downloadButton"
        @action="downloadExport"
        label="Exporter mes avis"
        class="font-medium !bg-white hover:text-secondary-500 text-secondary !h-[48px] ml-auto !px-6 gap-1.5 self-end md:self-auto">
        <template #icon>
          <i class="ga-icon ga-download text-xl" />
        </template>
      </AkButtonValidationV2>

      <div class="hidden md:flex justify-end p-2 gap-4">
        <button @click="platformScroll(-1)" class="rounded-full bg-white p-[5px] pb-1.5 pr-1.5">
          <i class="ga-icon ga-chevron_left text-[22px]" />
        </button>
        <button @click="platformScroll(1)" class="rounded-full bg-white p-[5px] pb-1.5 pl-1.5">
          <i class="ga-icon ga-chevron_right text-[22px]" />
        </button>
      </div>
    </div>
    <div
      class="flex gap-4 overflow-x-auto no-scrollbar platformScrollContainer snap-x snap-mandatory md:px-12 px-4"
      ref="platformScrollContainer">
      <div class="bg-primary p-4 pr-8 rounded-xl flex gap-6 items-center shrink-0 w-[303px] md:w-[390px]">
        <div class="flex flex-col gap-1 h-fit">
          <span class="text-white text-md font-medium leading-[21px] flex gap-4 items-center">
            <span class="bg-white rounded-[9px] flex items-center justify-center w-[50px] h-[50px]">
              <i class="ga-icon ga-star_sharp text-primary text-2xl" />
            </span>
            Note globale
          </span>
          <AkShimmer :loading="globalRating === null" width="180px" height="48px" color="#ffffff50">
            <div class="flex gap-2 items-center">
              <span class="text-white text-[30px] font-bold leading-[48px]">
                {{ globalRating }}
              </span>
              <span class="text-white text-[18px]">/10</span>
              <div class="w-1.5 h-1.5 rounded-full bg-white"></div>
              <span class="text-white text-md">{{ totalReviewCount }} avis</span>
            </div>
          </AkShimmer>
          <RatingStar
            v-if="globalRating !== null"
            :indicator="globalRating === '-' ? 0 : globalRating * 10"
            class="[&_.ga-icon.text-secondary]:text-[#FFAE20] w-fit"
            :outlined="false" />
          <AkShimmer v-else :loading="true" width="100px" height="16px" color="#ffffff50" />
        </div>
      </div>

      <div
        v-for="(item, index) of stats"
        :key="item?.reservationSite ?? index"
        class="flex flex-col gap-1 rounded-xl p-4 bg-white justify-center pl-6 shrink-0 min-w-[240px]">
        <AkShimmer :loading="!item" width="120px" height="32px">
          <div class="flex items-center gap-1.5">
            <i
              class="logo-site w-[32px] h-[32px]"
              :class="iconReservation(item.reservationSite)"
              v-tooltip.bottom="displayReservationSite(item.reservationSite)" />
            <span class="text-grey-900 font-medium mb-1">
              {{ displayReviewsReservationSite(item.reservationSiteKey) }}
            </span>
          </div>
        </AkShimmer>
        <AkShimmer :loading="!item" width="180px" height="42px">
          <div class="flex gap-2 items-center">
            <span class="text-grey-900 text-[30px] font-bold leading-[42px]">
              {{ this.formatAmountVirgule(item.globalRating) }}
            </span>
            <span class="text-grey-500 text-[18px]">/ {{ item.scale }}</span>
            <div class="w-[3px] h-[3px] rounded-full bg-grey-500"></div>
            <span class="text-grey-500 text-md">{{ item.nbTotalReviews }} avis</span>
          </div>
        </AkShimmer>
        <RatingStar
          v-if="item"
          :indicator="(item.globalRating / item.scale) * 100"
          class="[&_.ga-icon.text-secondary]:text-[#FFAE20] w-fit"
          :outlined="false" />
        <AkShimmer v-else :loading="true" width="100px" height="16px" />
      </div>
    </div>
  </div>
</template>

<style scoped></style>
